import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, put, del } from '@/lib/api'

// ==================== Types ====================

export type KnowledgeBaseType = 'company' | 'project'
export type KBPermission = 'none' | 'viewer' | 'editor' | 'admin'

export interface KnowledgeBase {
  id: string
  type: KnowledgeBaseType
  projectId: string | null
  name: string
  description: string | null
  createdAt: string
  updatedAt: string | null
  permission?: KBPermission
  documentCount?: number
  project?: {
    id: string
    name: string
  }
}

export interface Document {
  id: string
  knowledgeBaseId: string
  parentId: string | null
  title: string
  content: string
  contentPlain: string | null
  sortOrder: number
  depth: number
  createdBy: string
  updatedBy: string | null
  createdAt: string
  updatedAt: string | null
  tags?: string[]
  author?: {
    id: string
    name: string
    avatar: string | null
  }
  children?: Document[]
}

export interface DocumentVersion {
  id: string
  documentId: string
  versionNumber: number
  title: string
  content: string
  createdBy: string
  createdAt: string
  author?: {
    id: string
    name: string
  }
}

export interface VersionDiff {
  documentId: string
  fromVersion: number
  toVersion: number
  titleChanged: boolean
  contentDiff: DiffLine[]
}

export interface DiffLine {
  type: 'added' | 'removed' | 'unchanged'
  content: string
  lineNumber: number
}

export interface Template {
  id: string
  knowledgeBaseId: string | null
  name: string
  description: string | null
  content: string
  isBuiltIn: boolean
  createdBy: string | null
  createdAt: string
}

export interface KBComment {
  id: string
  documentId: string
  parentId: string | null
  content: string
  createdBy: string
  createdAt: string
  updatedAt: string | null
  author?: {
    id: string
    name: string
    avatar: string | null
  }
  replies?: KBComment[]
}

export interface SearchResult {
  id: string
  knowledgeBaseId: string
  knowledgeBaseName: string
  knowledgeBaseType: KnowledgeBaseType
  projectId: string | null
  projectName: string | null
  title: string
  excerpt: string
  highlightedTitle: string
  highlightedExcerpt: string
  createdBy: string
  authorName: string
  updatedAt: string | null
}

export interface CompanyEditor {
  id: string
  employeeId: string
  grantedBy: string
  createdAt: string
  employee?: {
    id: string
    name: string
    avatar: string | null
  }
}

export interface LinkedIssue {
  id: string
  title: string
  status: string
  projectId: string
  projectName?: string
}

// Input Types
export interface CreateDocumentInput {
  knowledgeBaseId: string
  parentId?: string
  title: string
  content: string
  tags?: string[]
  templateId?: string
}

export interface UpdateDocumentInput {
  title?: string
  content?: string
  tags?: string[]
}

export interface MoveDocumentInput {
  documentId: string
  targetKnowledgeBaseId?: string
  targetParentId?: string | null
}

export interface DocumentFilters {
  parentId?: string | null
  tag?: string
  page?: number
  pageSize?: number
}

export interface SearchFilters {
  type?: KnowledgeBaseType
  projectId?: string
  authorId?: string
  startDate?: string
  endDate?: string
  page?: number
  pageSize?: number
}

export interface CreateTemplateInput {
  knowledgeBaseId?: string
  name: string
  description?: string
  content: string
}

export interface AddCommentInput {
  documentId: string
  content: string
  parentId?: string
}

export interface PaginatedResult<T> {
  data: T[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ==================== Query Keys ====================

export const knowledgeBaseKeys = {
  all: ['knowledge-base'] as const,
  // Knowledge Bases
  knowledgeBases: () => [...knowledgeBaseKeys.all, 'knowledge-bases'] as const,
  knowledgeBasesList: () => [...knowledgeBaseKeys.knowledgeBases(), 'list'] as const,
  companyKB: () => [...knowledgeBaseKeys.knowledgeBases(), 'company'] as const,
  projectKB: (projectId: string) => [...knowledgeBaseKeys.knowledgeBases(), 'project', projectId] as const,
  // Documents
  documents: () => [...knowledgeBaseKeys.all, 'documents'] as const,
  documentsList: (knowledgeBaseId: string, filters?: DocumentFilters) =>
    [...knowledgeBaseKeys.documents(), 'list', knowledgeBaseId, filters] as const,
  document: (id: string) => [...knowledgeBaseKeys.documents(), 'detail', id] as const,
  // Versions
  versions: (documentId: string) => [...knowledgeBaseKeys.documents(), documentId, 'versions'] as const,
  version: (documentId: string, versionNumber: number) =>
    [...knowledgeBaseKeys.versions(documentId), versionNumber] as const,
  versionCompare: (documentId: string, from: number, to: number) =>
    [...knowledgeBaseKeys.versions(documentId), 'compare', from, to] as const,
  // Search
  search: (query: string, filters?: SearchFilters) =>
    [...knowledgeBaseKeys.all, 'search', query, filters] as const,
  // Templates
  templates: (knowledgeBaseId?: string) => [...knowledgeBaseKeys.all, 'templates', knowledgeBaseId] as const,
  // Comments
  comments: (documentId: string) => [...knowledgeBaseKeys.documents(), documentId, 'comments'] as const,
  // Company Editors
  companyEditors: () => [...knowledgeBaseKeys.all, 'company-editors'] as const,
  // Document Links
  documentLinks: (documentId: string) => [...knowledgeBaseKeys.documents(), documentId, 'links'] as const,
  issueDocuments: (issueId: string) => [...knowledgeBaseKeys.all, 'issues', issueId, 'documents'] as const,
}

// ==================== Knowledge Base Hooks ====================

/**
 * 获取可访问的知识库列表
 */
export function useKnowledgeBases() {
  return useQuery({
    queryKey: knowledgeBaseKeys.knowledgeBasesList(),
    queryFn: () => get<KnowledgeBase[]>('/knowledge-base'),
  })
}

/**
 * 获取公司知识库
 */
export function useCompanyKnowledgeBase() {
  return useQuery({
    queryKey: knowledgeBaseKeys.companyKB(),
    queryFn: () => get<KnowledgeBase>('/knowledge-base/company'),
  })
}

/**
 * 获取项目知识库
 */
export function useProjectKnowledgeBase(projectId: string) {
  return useQuery({
    queryKey: knowledgeBaseKeys.projectKB(projectId),
    queryFn: () => get<KnowledgeBase>(`/knowledge-base/projects/${projectId}`),
    enabled: !!projectId,
  })
}

// ==================== Document Hooks ====================

/**
 * 获取文档列表
 */
export function useDocuments(knowledgeBaseId: string, filters?: DocumentFilters) {
  return useQuery({
    queryKey: knowledgeBaseKeys.documentsList(knowledgeBaseId, filters),
    queryFn: () =>
      get<PaginatedResult<Document>>('/knowledge-base/documents', {
        knowledgeBaseId,
        ...filters,
      } as Record<string, unknown>),
    enabled: !!knowledgeBaseId,
  })
}

/**
 * 获取单个文档详情
 */
export function useDocument(id: string) {
  return useQuery({
    queryKey: knowledgeBaseKeys.document(id),
    queryFn: () => get<Document>(`/knowledge-base/documents/${id}`),
    enabled: !!id,
  })
}

/**
 * 创建文档
 */
export function useCreateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateDocumentInput) => post<Document>('/knowledge-base/documents', data),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.documentsList(result.knowledgeBaseId),
      })
    },
  })
}

/**
 * 更新文档
 */
export function useUpdateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDocumentInput }) =>
      put<Document>(`/knowledge-base/documents/${id}`, data),
    onSuccess: (result, { id }) => {
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.document(id) })
      void queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.documentsList(result.knowledgeBaseId),
      })
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.versions(id) })
    },
  })
}

/**
 * 删除文档
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/knowledge-base/documents/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.documents() })
    },
  })
}

/**
 * 移动文档
 */
export function useMoveDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, ...data }: MoveDocumentInput) =>
      post<null>(`/knowledge-base/documents/${documentId}/move`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.documents() })
    },
  })
}

// ==================== Version Hooks ====================

/**
 * 获取文档版本列表
 */
export function useVersions(documentId: string) {
  return useQuery({
    queryKey: knowledgeBaseKeys.versions(documentId),
    queryFn: () => get<DocumentVersion[]>(`/knowledge-base/documents/${documentId}/versions`),
    enabled: !!documentId,
  })
}

/**
 * 获取特定版本详情
 */
export function useVersion(documentId: string, versionNumber: number) {
  return useQuery({
    queryKey: knowledgeBaseKeys.version(documentId, versionNumber),
    queryFn: () =>
      get<DocumentVersion>(`/knowledge-base/documents/${documentId}/versions/${versionNumber}`),
    enabled: !!documentId && versionNumber > 0,
  })
}

/**
 * 比较两个版本
 */
export function useCompareVersions(documentId: string, fromVersion: number, toVersion: number) {
  return useQuery({
    queryKey: knowledgeBaseKeys.versionCompare(documentId, fromVersion, toVersion),
    queryFn: () =>
      get<VersionDiff>(`/knowledge-base/documents/${documentId}/compare`, {
        from: fromVersion,
        to: toVersion,
      }),
    enabled: !!documentId && fromVersion > 0 && toVersion > 0 && fromVersion !== toVersion,
  })
}

/**
 * 恢复文档版本
 */
export function useRestoreVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, versionNumber }: { documentId: string; versionNumber: number }) =>
      post<Document>(`/knowledge-base/documents/${documentId}/restore/${versionNumber}`),
    onSuccess: (result, { documentId }) => {
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.document(documentId) })
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.versions(documentId) })
      void queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.documentsList(result.knowledgeBaseId),
      })
    },
  })
}

// ==================== Search Hooks ====================

/**
 * 知识库全文搜索
 */
export function useKnowledgeBaseSearch(query: string, filters?: SearchFilters) {
  return useQuery({
    queryKey: knowledgeBaseKeys.search(query, filters),
    queryFn: () =>
      get<PaginatedResult<SearchResult>>('/knowledge-base/search', {
        q: query,
        ...filters,
      } as Record<string, unknown>),
    enabled: query.length >= 2,
    staleTime: 30000, // 30 秒缓存
  })
}

// ==================== Template Hooks ====================

/**
 * 获取模板列表
 */
export function useTemplates(knowledgeBaseId?: string) {
  return useQuery({
    queryKey: knowledgeBaseKeys.templates(knowledgeBaseId),
    queryFn: () =>
      get<Template[]>('/knowledge-base/templates', knowledgeBaseId ? { knowledgeBaseId } : undefined),
  })
}

/**
 * 创建模板
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTemplateInput) => post<Template>('/knowledge-base/templates', data),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.templates(result.knowledgeBaseId ?? undefined),
      })
    },
  })
}

/**
 * 从文档创建模板
 */
export function useCreateTemplateFromDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, name }: { documentId: string; name: string }) =>
      post<Template>('/knowledge-base/templates/from-document', { documentId, name }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.templates() })
    },
  })
}

// ==================== Collaboration Hooks ====================

/**
 * 获取文档评论列表
 */
export function useDocumentComments(documentId: string) {
  return useQuery({
    queryKey: knowledgeBaseKeys.comments(documentId),
    queryFn: () => get<KBComment[]>(`/knowledge-base/documents/${documentId}/comments`),
    enabled: !!documentId,
  })
}

/**
 * 添加评论
 */
export function useAddComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, content, parentId }: AddCommentInput) =>
      post<KBComment>(`/knowledge-base/documents/${documentId}/comments`, { content, parentId }),
    onSuccess: (_, { documentId }) => {
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.comments(documentId) })
    },
  })
}

/**
 * 关注文档
 */
export function useWatchDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (documentId: string) =>
      post<null>(`/knowledge-base/documents/${documentId}/watch`),
    onSuccess: (_, documentId) => {
      void queryClient.invalidateQueries({
        queryKey: [...knowledgeBaseKeys.document(documentId), 'watching'],
      })
    },
  })
}

/**
 * 取消关注文档
 */
export function useUnwatchDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (documentId: string) =>
      del<null>(`/knowledge-base/documents/${documentId}/watch`),
    onSuccess: (_, documentId) => {
      void queryClient.invalidateQueries({
        queryKey: [...knowledgeBaseKeys.document(documentId), 'watching'],
      })
    },
  })
}

/**
 * 检查是否关注文档
 */
export function useIsWatchingDocument(documentId: string) {
  return useQuery({
    queryKey: [...knowledgeBaseKeys.document(documentId), 'watching'] as const,
    queryFn: () => get<{ watching: boolean }>(`/knowledge-base/documents/${documentId}/watching`),
    enabled: !!documentId,
  })
}

// ==================== Company Editor Hooks ====================

/**
 * 获取公司知识库编辑者列表
 */
export function useCompanyEditors() {
  return useQuery({
    queryKey: knowledgeBaseKeys.companyEditors(),
    queryFn: () => get<CompanyEditor[]>('/knowledge-base/company/editors'),
  })
}

/**
 * 添加公司知识库编辑者
 */
export function useAddCompanyEditor() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (employeeId: string) =>
      post<null>('/knowledge-base/company/editors', { employeeId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.companyEditors() })
    },
  })
}

/**
 * 移除公司知识库编辑者
 */
export function useRemoveCompanyEditor() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (editorId: string) =>
      del<null>(`/knowledge-base/company/editors/${editorId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.companyEditors() })
    },
  })
}

// ==================== Document-Issue Link Hooks ====================

/**
 * 获取文档关联的任务
 */
export function useLinkedIssues(documentId: string) {
  return useQuery({
    queryKey: knowledgeBaseKeys.documentLinks(documentId),
    queryFn: () => get<LinkedIssue[]>(`/knowledge-base/documents/${documentId}/links`),
    enabled: !!documentId,
  })
}

/**
 * 获取任务关联的文档
 */
export function useLinkedDocuments(issueId: string) {
  return useQuery({
    queryKey: knowledgeBaseKeys.issueDocuments(issueId),
    queryFn: () => get<Document[]>(`/knowledge-base/issues/${issueId}/documents`),
    enabled: !!issueId,
  })
}

/**
 * 关联文档到任务
 */
export function useLinkDocumentToIssue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, issueId }: { documentId: string; issueId: string }) =>
      post<null>(`/knowledge-base/documents/${documentId}/links`, { issueId }),
    onSuccess: (_, { documentId, issueId }) => {
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.documentLinks(documentId) })
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.issueDocuments(issueId) })
    },
  })
}

/**
 * 取消文档与任务的关联
 */
export function useUnlinkDocumentFromIssue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, issueId }: { documentId: string; issueId: string }) =>
      del<null>(`/knowledge-base/documents/${documentId}/links/${issueId}`),
    onSuccess: (_, { documentId, issueId }) => {
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.documentLinks(documentId) })
      void queryClient.invalidateQueries({ queryKey: knowledgeBaseKeys.issueDocuments(issueId) })
    },
  })
}

// ==================== Export Hooks ====================

/**
 * 上传知识库图片
 * 支持拖拽上传和粘贴上传
 */
export function useUploadKnowledgeBaseImage() {
  return useMutation({
    mutationFn: async ({
      file,
      documentId,
      knowledgeBaseId,
    }: {
      file: File
      documentId?: string
      knowledgeBaseId?: string
    }): Promise<{ url: string; id: string }> => {
      // Import image processing utilities
      const { processFileForUpload } = await import('@/lib/image-utils')

      // Process file (convert to WebP if needed)
      const processed = await processFileForUpload(file)

      // Build FormData
      const formData = new FormData()
      formData.append('file', processed.blob, processed.fileName)

      // Build query params
      const params = new URLSearchParams()
      params.set('category', 'knowledge')
      if (documentId) {
        params.set('relatedType', 'document')
        params.set('relatedId', documentId)
      } else if (knowledgeBaseId) {
        params.set('relatedType', 'knowledge-base')
        params.set('relatedId', knowledgeBaseId)
      }

      // Upload file
      const response = await fetch(`/api/v1/files/upload?${params.toString()}`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })

      if (!response.ok) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorData = (await response.json()) as any
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
        throw new Error(errorData.error?.message || '图片上传失败')
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = (await response.json()) as any
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const fileData = result.data as { id: string; storagePath: string }

      // Get signed URL for the uploaded image
      const urlResponse = await fetch(`/api/v1/files/${fileData.id}/url`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })

      if (!urlResponse.ok) {
        // Fallback to direct download URL if signed URL fails
        return {
          id: fileData.id,
          url: `/api/v1/files/${fileData.id}/download`,
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const urlResult = (await urlResponse.json()) as any
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const signedUrl = urlResult.data as { url: string; expiresAt: string }

      return {
        id: fileData.id,
        url: signedUrl.url,
      }
    },
  })
}

/**
 * 导出文档为 PDF (HTML)
 * 在新窗口打开可打印的 HTML 页面
 */
export function useExportDocumentPdf() {
  return useMutation({
    mutationFn: async () => {
      // 触发浏览器打印对话框
      // 样式由 @media print 控制，隐藏侧边栏等无关元素
      window.print()
      return { success: true }
    },
  })
}

/**
 * 导出文档为 Markdown
 * 返回下载 URL 或直接触发下载
 */
export function useExportDocumentMarkdown() {
  return useMutation({
    mutationFn: async (documentId: string) => {
      // 获取最新文档内容
      const doc = await get<Document>(`/knowledge-base/documents/${documentId}`)

      // 构建 Markdown 内容
      const frontmatter = [
        '---',
        `title: ${doc.title}`,
        `author: ${doc.author?.name ?? 'Unknown'}`,
        `date: ${doc.createdAt}`,
        `updated: ${doc.updatedAt ?? doc.createdAt}`,
        '---',
        '',
        ''
      ].join('\n')

      const fullContent = frontmatter + doc.content

      // 创建并下载文件
      const blob = new Blob([fullContent], { type: 'text/markdown;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${doc.title}.md`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
  })
}

// ==================== Share Link Types ====================

export interface ShareLink {
  id: string
  documentId: string
  token: string
  hasPassword: boolean
  expiresAt: string | null
  allowComments: boolean
  viewCount: number
  createdBy: string
  createdAt: string
}

export interface CreateShareLinkInput {
  documentId: string
  password?: string
  expiresAt?: string
  allowComments?: boolean
}

export interface SharedDocument {
  id: string
  title: string
  content: string
  author?: { name: string }
  updatedAt: string | null
  createdAt: string
  allowComments: boolean
}

// ==================== Share Link Hooks ====================

/**
 * 获取文档的分享链接列表
 */
export function useShareLinks(documentId: string) {
  return useQuery({
    queryKey: [...knowledgeBaseKeys.document(documentId), 'share-links'] as const,
    queryFn: () => get<ShareLink[]>(`/knowledge-base/share/document/${documentId}`),
    enabled: !!documentId,
  })
}

/**
 * 创建分享链接
 */
export function useCreateShareLink() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateShareLinkInput) =>
      post<ShareLink>('/knowledge-base/share', data),
    onSuccess: (_, { documentId }) => {
      void queryClient.invalidateQueries({
        queryKey: [...knowledgeBaseKeys.document(documentId), 'share-links'],
      })
    },
  })
}

/**
 * 删除分享链接
 */
export function useDeleteShareLink() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ linkId, documentId: _documentId }: { linkId: string; documentId: string }) =>
      del<null>(`/knowledge-base/share/${linkId}`),
    onSuccess: (_, { documentId }) => {
      void queryClient.invalidateQueries({
        queryKey: [...knowledgeBaseKeys.document(documentId), 'share-links'],
      })
    },
  })
}

/**
 * 检查分享链接是否需要密码
 */
export function useCheckShareLink(token: string) {
  return useQuery({
    queryKey: ['share-link', token, 'check'] as const,
    queryFn: () => get<{ requiresPassword: boolean }>(`/knowledge-base/share/public/${token}/check`),
    enabled: !!token,
  })
}

/**
 * 获取分享的文档
 */
export function useGetSharedDocument() {
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password?: string }) =>
      post<SharedDocument>(`/knowledge-base/share/public/${token}`, { password }),
  })
}

// ==================== Legacy Compatibility (deprecated) ====================
// These are kept for backward compatibility but should be migrated to new hooks

/** @deprecated Use useKnowledgeBases instead */
export function useSpaces() {
  return useKnowledgeBases()
}

/** @deprecated Use KnowledgeBase type instead */
export type Space = KnowledgeBase
/** @deprecated Use KBPermission type instead */
export type SpaceMemberRole = KBPermission
/** @deprecated Use KnowledgeBaseType type instead */
export type SpaceVisibility = 'public' | 'private'

/** @deprecated No longer needed - use useKnowledgeBases */
export function useCreateSpace() {
  // This is a no-op since spaces are now auto-created
  return useMutation({
    mutationFn: async () => {
      throw new Error('Spaces are now auto-created. Use project or company knowledge base.')
    },
  })
}
